/**
 * PowerPoint export utility for Platinum charts
 * Pure functions - no React, no hooks
 */
import PptxGenJS from 'pptxgenjs'
import type { ParsedTable, GraphConfig, ChartType, DataType } from '../components/platinum-graphs/types'
import { filterTableData } from '../components/platinum-graphs/utils/chartFilters'
import { transposeParsedTable } from '../components/platinum-graphs/utils/convertPlatinumData'
import { getChartColorByIndex, toNumber } from '../components/platinum-graphs/generators/helpers'
import {
  getSelectedRowLabels,
  getSelectedColumnLabels,
  getChartRowPositions,
} from '../components/platinum-graphs/utils/selectionHelpers'
import {
  isBarFamilyChartType,
  isHorizontalBarChartType,
  sortTableForChart,
} from '../components/platinum-graphs/utils/chartSort'
import type { ChartDesign } from '../types/gotypes'
import { stampLine } from './outputStamp'
import { balancedAxisLimit } from './applyChartDesign'

/**
 * Apply graph filters (rows, columns, axis transpose, single-column) to a ParsedTable.
 * Mirrors the logic in generateGraphOptions.ts + selectionHelpers.ts (Base row exclusion).
 */
export function applyGraphFilters(
  table: ParsedTable,
  config: GraphConfig,
  allRowLabels: string[],
  allRowTypes: string[],
  allColumnLabels: string[],
  allColumnTypes: string[],
): ParsedTable {
  let filtered = table

  // Transpose first if switchAxis
  if (config.switchAxis) {
    filtered = transposeParsedTable(filtered)
  }

  // Resolve effective row/column selections — same logic as PlatinumGraphs.tsx.
  // getSelectedRowLabels defaults to non-Base rows when selection is empty.
  const resolvedRows = getSelectedRowLabels(
    config.selectedRows || [],
    allRowLabels,
    allRowTypes
  )
  const resolvedCols = getSelectedColumnLabels(
    config.selectedColumns || [],
    allColumnLabels,
    allColumnTypes
  )

  // Swap when axis is switched
  const effectiveRows = config.switchAxis ? resolvedCols : resolvedRows
  const effectiveCols = config.switchAxis ? resolvedRows : resolvedCols
  // #1808: rows by POSITION, as the chart picks them (PlatinumGraphs), so a
  // repeated row label exports the same rows the chart shows.
  const effectiveRowPositions = config.switchAxis
    ? getChartRowPositions(config.selectedColumns || [], allColumnLabels, allColumnTypes)
    : getChartRowPositions(config.selectedRows || [], allRowLabels, allRowTypes)

  // Apply row/column filters
  if (effectiveRows.length > 0 || effectiveCols.length > 0) {
    filtered = filterTableData(filtered, effectiveRows, effectiveCols, effectiveRowPositions)
  }

  // For bar/column with a single selected column, reduce to that one column only
  if (
    config.selectedColumn &&
    (config.chartType === 'bar' || config.chartType === 'column')
  ) {
    const colIndex = filtered.headers.findIndex(h => h === config.selectedColumn)
    if (colIndex > 0) {
      filtered = {
        headers: ['', config.selectedColumn],
        rows: filtered.rows.map(row => [row[0], row[colIndex] ?? '0']),
        rowCnt: filtered.rowCnt,
        colCnt: 2,
      }
    }
  }

  return filtered
}

/**
 * Puts a filtered table in the order the chart draws it (#1808) when the
 * widget has a sort_order. PowerPoint draws a horizontal bar chart's first
 * category at the BOTTOM, so for the horizontal types the reading order
 * (top-down) is reversed. Absent sort_order — or a chart outside the bar
 * family — returns the table unchanged, as exported before.
 */
export function orderTableForPptx(table: ParsedTable, config: GraphConfig): ParsedTable {
  if (!config.sortOrder || !isBarFamilyChartType(config.chartType)) return table
  const sorted = sortTableForChart(table, config.chartType, config.sortOrder)
  if (!isHorizontalBarChartType(config.chartType)) return sorted
  return { ...sorted, rows: [...sorted.rows].reverse() }
}

/**
 * "Balanced around 0" on the exported chart (#1808): the same limit
 * applyChartDesign puts on the live chart, sized on the exported values
 * (stacked charts on each bar's positive and negative sums), as
 * valAxisMinVal/valAxisMaxVal.
 */
export function buildBalancedAxisPptxOpts(
  design: ChartDesign | undefined,
  table: ParsedTable,
  chartType: ChartType,
  dataType?: DataType,
): Record<string, number> {
  const on = (design?.y_axis?.balanced_zero ?? design?.x_axis?.balanced_zero) === true
  if (!on || !isBarFamilyChartType(chartType)) return {}
  const cell = (row: string[], c: number) => {
    const v = toNumber(row[c])
    return v === null || Number.isNaN(v) ? null : v
  }
  const columns = table.headers.slice(1).map((_, i) => i + 1)
  // A stack is one row (stackedBar) or one column (stackedColumn).
  const stacks: (number | null)[][] =
    chartType === 'stackedBar'
      ? table.rows.map((row) => columns.map((c) => cell(row, c)))
      : chartType === 'stackedColumn'
        ? columns.map((c) => table.rows.map((row) => cell(row, c)))
        : table.rows.flatMap((row) => columns.map((c) => [cell(row, c)]))
  const lengths = stacks.flatMap((stack) => {
    const nums = stack.filter((v): v is number => v !== null)
    if (nums.length === 0) return []
    return [nums.filter((v) => v >= 0).reduce((a, b) => a + b, 0), nums.filter((v) => v < 0).reduce((a, b) => a + b, 0)]
  })
  const limit = balancedAxisLimit(lengths, dataType)
  return { valAxisMinVal: -limit, valAxisMaxVal: limit }
}

interface PptxChartSeries {
  name: string
  labels?: string[]
  values: number[] | Array<{ x: number; y: number }>
}

/**
 * Convert a filtered ParsedTable to pptxgenjs chart series data.
 */
export function buildPptxChartData(
  table: ParsedTable,
  chartType: ChartType
): PptxChartSeries[] {
  const headers = table.headers.slice(1) // exclude first empty header
  const rows = table.rows

  if (chartType === 'pie') {
    return [{
      name: headers[0] || 'Data',
      labels: rows.map(row => row[0]),
      values: rows.map(row => parseFloat(row[1] || '0') || 0),
    }]
  }

  if (chartType === 'scatter') {
    return [{
      name: 'Data',
      values: rows.map(row => ({
        x: parseFloat(row[1] || '0') || 0,
        y: parseFloat(row[2] || '0') || 0,
      })),
    }]
  }

  // Horizontal bar charts: rows are Y-axis categories, columns are series.
  // pptxgenjs uses `labels` as the axis categories, so we pivot: each column = one series.
  if (chartType === 'bar' || chartType === 'stackedBar' || chartType === 'clusteredBar') {
    return headers.map((colHeader, colIdx) => ({
      name: colHeader,
      labels: rows.map(row => row[0]),
      values: rows.map(row => parseFloat(row[colIdx + 1] || '0') || 0),
    }))
  }

  // Vertical column / line / stacked column / clustered column:
  // columns are X-axis categories, rows are series.
  return rows.map(row => ({
    name: row[0] || '',
    labels: headers,
    values: headers.map((_, i) => parseFloat(row[i + 1] || '0') || 0),
  }))
}

interface ChartTypeConfig {
  type: string
  opts: Record<string, unknown>
}

/**
 * Map Platinum ChartType to pptxgenjs chart type string + options.
 */
export function getChartTypeOptions(
  chartType: ChartType,
  colors?: string[]
): ChartTypeConfig {
  const colorOpts = colors && colors.length > 0 ? { chartColors: colors } : {}

  switch (chartType) {
    case 'bar':
      return { type: 'bar', opts: { barDir: 'bar', ...colorOpts } }
    case 'column':
      return { type: 'bar', opts: { barDir: 'col', ...colorOpts } }
    case 'line':
      return { type: 'line', opts: { ...colorOpts } }
    case 'pie':
      return { type: 'pie', opts: { ...colorOpts } }
    case 'stackedBar':
      return { type: 'bar', opts: { barDir: 'bar', barGrouping: 'stacked', ...colorOpts } }
    case 'stackedColumn':
      return { type: 'bar', opts: { barDir: 'col', barGrouping: 'stacked', ...colorOpts } }
    case 'clusteredBar':
      return { type: 'bar', opts: { barDir: 'bar', barGrouping: 'clustered', ...colorOpts } }
    case 'clusteredColumn':
      return { type: 'bar', opts: { barDir: 'col', barGrouping: 'clustered', ...colorOpts } }
    case 'scatter':
      return { type: 'scatter', opts: { ...colorOpts } }
    default:
      return { type: 'bar', opts: { barDir: 'col', ...colorOpts } }
  }
}

/**
 * 'bar', 'stackedBar', 'clusteredBar' render horizontally — the generator's
 * xAxis carries VALUES and yAxis carries CATEGORIES (see bar.ts/stackedBar.ts
 * /clusteredBar.ts), the opposite of every other chart type. pptxgenjs's
 * cat/val axis options are orientation-invariant, so ChartDesign's x_axis/
 * y_axis must be swapped onto cat/val for these three (#1044).
 */
function isHorizontalChartType(chartType: ChartType): boolean {
  return chartType === 'bar' || chartType === 'stackedBar' || chartType === 'clusteredBar'
}

function stripHash(color: string): string {
  return color.replace('#', '')
}

function pptxGridlineStyle(style: string | undefined): 'solid' | 'dash' | 'dot' | undefined {
  if (style === 'dashed') return 'dash'
  if (style === 'dotted') return 'dot'
  if (style === 'solid') return 'solid'
  return undefined
}

function pptxLegendPos(position: string | undefined): 'b' | 't' | 'l' | 'r' | undefined {
  switch (position) {
    case 'top': return 't'
    case 'bottom': return 'b'
    case 'left': return 'l'
    case 'right': return 'r'
    default: return undefined
  }
}

/** Mirrors buildDataLabelFormatter's 'auto' rule in applyChartDesign.ts (#1044). */
function pptxDataLabelFormatCode(
  numberFormat: string | undefined,
  dataType: DataType | undefined,
): string | undefined {
  const effective =
    !numberFormat || numberFormat === 'auto'
      ? dataType === 'freq' || dataType == null ? 'integer' : 'percent0'
      : numberFormat
  switch (effective) {
    case 'integer': return '#,##0'
    case 'percent0': return '0%'
    case 'percent1': return '0.0%'
    case 'percent2': return '0.00%'
    default: return undefined
  }
}

/**
 * Mirrors a widget's ChartDesign (#968 Part C axis/gridlines, #1044 legend/
 * data-labels) onto pptxgenjs chart options — "as closely as pptxgenjs
 * allows". Gaps, documented rather than silently dropped:
 *   - axis/gridline `show` is never toggled here either, matching
 *     applyChartDesign.ts's rule that styling a gridline never turns it on.
 *   - data-label number format maps to one of four Excel format codes
 *     (#,##0 / 0% / 0.0% / 0.00%); there is no arbitrary-format equivalent.
 *   - legend/axis font FAMILY has no override surface in either place, so
 *     nothing is lost there, but pptxgenjs has no per-axis-title font-weight
 *     control either (see buildChartTitlePptxOpts for the title itself).
 */
export function buildChartDesignPptxOpts(
  design: ChartDesign | undefined,
  chartType: ChartType,
  dataType?: DataType,
): Record<string, unknown> {
  if (!design) return {}
  const opts: Record<string, unknown> = {}
  const horizontal = isHorizontalChartType(chartType)
  const catAxis = horizontal ? design.y_axis : design.x_axis
  const valAxis = horizontal ? design.x_axis : design.y_axis

  if (catAxis?.label_font_size != null) opts.catAxisLabelFontSize = catAxis.label_font_size
  if (catAxis?.label_color != null) opts.catAxisLabelColor = stripHash(catAxis.label_color)
  if (catAxis?.title_text) {
    opts.catAxisTitle = catAxis.title_text
    opts.showCatAxisTitle = true
  }
  if (catAxis?.title_color != null) opts.catAxisTitleColor = stripHash(catAxis.title_color)

  if (valAxis?.label_font_size != null) opts.valAxisLabelFontSize = valAxis.label_font_size
  if (valAxis?.label_color != null) opts.valAxisLabelColor = stripHash(valAxis.label_color)
  if (valAxis?.title_text) {
    opts.valAxisTitle = valAxis.title_text
    opts.showValAxisTitle = true
  }
  if (valAxis?.title_color != null) opts.valAxisTitleColor = stripHash(valAxis.title_color)

  if (design.gridlines) {
    const gridLine: Record<string, unknown> = {}
    if (design.gridlines.color != null) gridLine.color = stripHash(design.gridlines.color)
    if (design.gridlines.width != null) gridLine.size = design.gridlines.width
    const style = pptxGridlineStyle(design.gridlines.dash_style)
    if (style) gridLine.style = style
    if (Object.keys(gridLine).length > 0) {
      // Same convention as applyChartDesign.ts: one gridlines override
      // patches BOTH axes, visibility untouched.
      opts.catGridLine = gridLine
      opts.valGridLine = gridLine
    }
  }

  if (design.legend?.show != null) opts.showLegend = design.legend.show
  const legendPos = pptxLegendPos(design.legend?.position)
  if (legendPos) opts.legendPos = legendPos
  if (design.legend?.font_size != null) opts.legendFontSize = design.legend.font_size
  if (design.legend?.font_color != null) opts.legendColor = stripHash(design.legend.font_color)

  if (design.data_labels?.show != null) opts.showValue = design.data_labels.show
  if (design.data_labels?.show) {
    const formatCode = pptxDataLabelFormatCode(design.data_labels.number_format, dataType)
    if (formatCode) opts.dataLabelFormatCode = formatCode
  }

  return opts
}

/**
 * Mirrors ChartTitleDesign onto the pptxgenjs `addText` options for the
 * title text box (#1044). `font_weight` only survives as bold/not-bold —
 * pptxgenjs text has no numeric-weight equivalent.
 */
export function buildChartTitlePptxOpts(design: ChartDesign | undefined): Record<string, unknown> {
  const t = design?.title
  if (!t) return {}
  const opts: Record<string, unknown> = {}
  if (t.align != null) opts.align = t.align
  if (t.font_size != null) opts.fontSize = t.font_size
  if (t.font_weight != null) opts.bold = t.font_weight >= 600
  if (t.color != null) opts.color = stripHash(t.color)
  return opts
}

export interface ExportChartParams {
  table: ParsedTable
  config: GraphConfig
  allRowLabels: string[]
  allRowTypes: string[]
  allColumnLabels: string[]
  allColumnTypes: string[]
  colorMap?: Record<string, string>
  title?: string
  includeDataTable?: boolean
  /** Chart design overrides (#968 Part C, #1044) mirrored onto the exported deck. */
  design?: ChartDesign
  /**
   * Who exported it and from where (#1650): drawn at the foot of every slide,
   * written into every slide's notes (notes travel with a copied slide) and
   * the deck's author and subject.
   */
  stamp?: { who?: string; ref?: string; when?: Date }
}

// LAYOUT_WIDE is 13.33in by 7.5in; the stamp sits under the chart (which
// ends at 6.7in), right-aligned.
const STAMP_BOX = { x: 0.5, y: 7.05, w: 12.33, h: 0.3 }

/** Draws the stamp line on a slide and puts it in the slide's notes. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function addStampToSlide(slide: any, line: string): void {
  slide.addText(line, { ...STAMP_BOX, fontSize: 8, color: '808080', align: 'right', valign: 'bottom' })
  slide.addNotes(line)
}

/**
 * Main entry point: export a Platinum chart to a native .pptx file.
 * Triggers a browser download.
 */
export async function exportChartToPptx(params: ExportChartParams): Promise<void> {
  const {
    table, config,
    allRowLabels, allRowTypes, allColumnLabels, allColumnTypes,
    colorMap, title = 'Chart', includeDataTable = false, design, stamp,
  } = params

  // Apply filters (rows, columns, axis transpose, single-column, base
  // exclusion), then the widget's bar order (#1808).
  const filteredTable = orderTableForPptx(
    applyGraphFilters(table, config, allRowLabels, allRowTypes, allColumnLabels, allColumnTypes),
    config,
  )

  // Resolve series colors (strip # for pptxgenjs)
  const seriesNames = filteredTable.rows.map(r => r[0])
  const colors = seriesNames.map((name, index) => {
    if (colorMap) {
      const mapped = colorMap[name.toLowerCase()] || colorMap[name]
      if (mapped) return mapped.replace('#', '')
    }
    return getChartColorByIndex(index).replace('#', '')
  })

  const chartData = buildPptxChartData(filteredTable, config.chartType)
  const { type, opts } = getChartTypeOptions(config.chartType, colors)

  const pptx = new PptxGenJS()
  pptx.layout = 'LAYOUT_WIDE'
  const line = stamp ? stampLine({ who: stamp.who, ref: stamp.ref, when: stamp.when ?? new Date() }) : ''
  if (line) {
    if (stamp?.who) pptx.author = stamp.who
    pptx.company = 'Platinum'
    pptx.subject = line
  }

  // Slide 1: native chart
  const slide1 = pptx.addSlide()

  if (title) {
    slide1.addText(title, {
      x: 0.5, y: 0.1, w: 12, h: 0.6,
      fontSize: 20, bold: true, color: '333333',
      ...buildChartTitlePptxOpts(design),
    })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  slide1.addChart(type as any, chartData as any, {
    x: 0.5,
    y: 0.9,
    w: 12,
    h: 5.8,
    showLegend: true,
    legendPos: 'b',
    showTitle: false,
    ...opts,
    ...buildChartDesignPptxOpts(design, config.chartType, config.dataType),
    // #1808. Bar colour rules are NOT exported: pptxgenjs colours a bar
    // chart per series, not per bar (see docs/dashboards.md).
    ...buildBalancedAxisPptxOpts(design, filteredTable, config.chartType, config.dataType),
  })
  if (line) addStampToSlide(slide1, line)

  // Optional slide 2: data table
  if (includeDataTable && filteredTable.rows.length > 0) {
    const slide2 = pptx.addSlide()

    if (title) {
      slide2.addText(`${title} - Data`, {
        x: 0.5, y: 0.1, w: 12, h: 0.6,
        fontSize: 20, bold: true, color: '333333',
      })
    }

    const headerRow = filteredTable.headers.map(h => ({
      text: h,
      options: {
        bold: true,
        fill: { color: '4E79A7' },
        color: 'FFFFFF',
        fontSize: 10,
        align: 'center' as const,
      },
    }))

    const dataRows = filteredTable.rows.map((row, rowIdx) =>
      row.map((cell, colIdx) => ({
        text: cell,
        options: {
          fontSize: 9,
          fill: { color: rowIdx % 2 === 0 ? 'FFFFFF' : 'F7F9FB' },
          align: (colIdx === 0 ? 'left' : 'right') as 'left' | 'right',
        },
      }))
    )

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    slide2.addTable([headerRow, ...dataRows] as any, {
      x: 0.5, y: 0.9, w: 12,
      border: { type: 'solid', pt: 0.5, color: 'DDDDDD' },
      rowH: 0.3,
    })
    if (line) addStampToSlide(slide2, line)
  }

  await pptx.writeFile({ fileName: `${title}.pptx` })
}
